import { ApiUploadXml } from '../api/front/xml/api-upload-xml';
import { apiActions } from '../slices/api-slice';
import { AppDispatch } from '../app/store';

/**
 * プリント商材の数量(pageOutputCount)変更に伴うXMLアップロードを
 * 「デバウンス + 変更分の集約(coalesce) + 直列送信」で管理するキュー。
 */

export type UploadFile = {
  filename: string;
  body: string;
  is_create: '0' | '1';
};

type QueueState = {
  kijshopCd: string;
  dispatch: AppDispatch;
  // 未送信ファイル(filename -> 最新内容)。同一ファイルへの連打はここで最新に上書きされる。
  dirty: Map<string, UploadFile>;
  timer: ReturnType<typeof setTimeout> | null;
  uploading: boolean;
  // flush完了待ちのresolver(発注/離脱時の待機者)。引数は「未送信分を送り切れたか(成功=true)」。
  waiters: Array<(ok: boolean) => void>;
};

// 注文(shopOrderId)ごとのキュー。1画面1注文だが、別注文への取り違え防止のためキー分けする。
const queues: Record<string, QueueState> = {};

// デバウンス待ち時間(ms)。長押し自動連射(50ms間隔)が収まってから送る。
const DEBOUNCE_MS = 400;

const getQueue = (shopOrderId: string, kijshopCd: string, dispatch: AppDispatch): QueueState => {
  const exist = queues[shopOrderId];
  if (exist) {
    // 呼び出し元の最新の kijshopCd / dispatch を反映(アンマウント後も有効な store dispatch)
    exist.kijshopCd = kijshopCd;
    exist.dispatch = dispatch;
    return exist;
  }
  const created: QueueState = {
    kijshopCd,
    dispatch,
    dirty: new Map(),
    timer: null,
    uploading: false,
    waiters: [],
  };
  queues[shopOrderId] = created;
  return created;
};

const isIdle = (q: QueueState) => !q.uploading && q.dirty.size === 0;

// flush待機者(発注/離脱)をまとめて解決する。ok = 未送信分を送り切れたか(成功=true)。
// 解決前にwaitersを空にしておき、resolve中の再入(再enqueue等)で取りこぼさないようにする。
const settleWaiters = (q: QueueState, ok: boolean) => {
  const waiters = q.waiters;
  q.waiters = [];
  waiters.forEach((resolve) => resolve(ok));
};

// 完全に空(待機者・タイマー無し)になったキューは破棄し、無制限な蓄積を防ぐ。
const cleanupIfIdle = (shopOrderId: string) => {
  const q = queues[shopOrderId];
  if (q && isIdle(q) && q.waiters.length === 0 && !q.timer) {
    delete queues[shopOrderId];
  }
};

// 未送信分を1回送信する(送信中なら何もしない=直列化)
const startUpload = (shopOrderId: string) => {
  const q = queues[shopOrderId];
  if (!q || q.uploading || q.dirty.size === 0) {
    return;
  }
  // 送信対象をスナップショットし、dirtyは空に(送信中に来た変更は次サイクルへ)
  const sending = Array.from(q.dirty.values());
  q.dirty.clear();
  q.uploading = true;
  q.dispatch(apiActions.run(
    new ApiUploadXml(q.kijshopCd, shopOrderId, { files: sending }),
    {
      onSuccess: () => onSettled(shopOrderId, sending, false),
      onError: () => onSettled(shopOrderId, sending, true),
    },
    { ignoreSystemError: true },
  ));
};

// 1回の送信が終わった後の共通処理(成功/失敗)
const onSettled = (shopOrderId: string, sending: UploadFile[], errored: boolean) => {
  const q = queues[shopOrderId];
  if (!q) {
    return;
  }
  q.uploading = false;
  if (errored) {
    // 失敗分を戻す。ただし送信中に新しい変更が来ていればそちらを優先(上書きしない)。
    // 自動リトライはしない(次の変更/flushで再送される)。
    sending.forEach((f) => {
      if (!q.dirty.has(f.filename)) {
        q.dirty.set(f.filename, f);
      }
    });
    // flush待機者に「送り切れなかった(false)」を通知 → 発注側で発注を中断する。
    settleWaiters(q, false);
    cleanupIfIdle(shopOrderId);
    return;
  }
  if (q.dirty.size > 0) {
    // 送信中に来た変更を続けて送る(直列)
    startUpload(shopOrderId);
    return;
  }
  // 未送信分をすべて送り切れた → 待機者に成功(true)を通知(発注/離脱をハングさせない)。
  settleWaiters(q, true);
  cleanupIfIdle(shopOrderId);
};

/**
 * 数量変更ぶんのアップロードを積む(即時送信せず、デバウンスで集約)。
 * メモリ上のXMLモデル更新は呼び出し側で即時に行う前提(表示はそのまま)。
 */
export const enqueueOutputCountUpload = (
  shopOrderId: string,
  kijshopCd: string,
  dispatch: AppDispatch,
  files: UploadFile[],
) => {
  const q = getQueue(shopOrderId, kijshopCd, dispatch);
  // filename単位で最新内容に上書き(連打/同一ファイルの集約)
  files.forEach((f) => q.dirty.set(f.filename, f));
  if (q.timer) {
    clearTimeout(q.timer);
  }
  q.timer = setTimeout(() => {
    q.timer = null;
    startUpload(shopOrderId);
  }, DEBOUNCE_MS);
};

/**
 * 未送信分を即時に送り切り、その成否を解決するPromiseを返す。
 * - resolve(true)  : 未送信分を全て送り切れた(元々未送信が無い場合も含む)
 * - resolve(false) : 送信に失敗した(通信エラー等。失敗分はdirtyに残り次回再送可能)
 * 発注処理の冒頭・画面離脱時に await して、サーバへ最新枚数が反映済みであることを保証する。
 * 発注側は false のとき、古い枚数での発注を避けるため発注を中断する。
 */
export const flushOutputCountUpload = (shopOrderId: string): Promise<boolean> => {
  const q = queues[shopOrderId];
  if (!q || isIdle(q)) {
    return Promise.resolve(true);
  }
  if (q.timer) {
    clearTimeout(q.timer);
    q.timer = null;
  }
  return new Promise<boolean>((resolve) => {
    q.waiters.push(resolve);
    if (!q.uploading) {
      startUpload(shopOrderId);
    }
  });
};
